import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserRound, Coins, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Input } from '../ui/input';
import { usePlayer } from './PlayerAuth';
import { playerApi } from '../../lib/api';
import { useBrand } from '../BrandProvider';

function GoogleButton() {
  const go = () => {
    sessionStorage.setItem('titan.authReturn', window.location.pathname);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button data-testid="auth-google-btn" onClick={go} className="w-full flex items-center justify-center gap-3 rounded-xl bg-white text-slate-900 py-3 text-sm font-semibold hover:bg-slate-100 transition-colors">
      <svg viewBox="0 0 48 48" className="h-5 w-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 38.2 44 33 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
      Continua con Google
    </button>
  );
}

function AppleButton() {
  const [st, setSt] = useState(null);
  const go = () => {
    sessionStorage.setItem('titan.authReturn', window.location.pathname);
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/apple/start`;
  };
  useEffect(() => { playerApi.providers().then((p) => setSt(p.apple)).catch(() => setSt({ enabled: false, reason: 'Stato non disponibile offline' })); }, []);
  return (
    <div data-testid="auth-apple-status" className="space-y-1">
      <button data-testid="auth-apple-btn" disabled={!st?.enabled} aria-disabled={!st?.enabled} onClick={go}
        className="w-full flex items-center justify-center gap-3 rounded-xl bg-black text-white border border-white/20 py-3 text-sm font-semibold disabled:opacity-45 disabled:cursor-not-allowed">
        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true"><path d="M16.37 12.6c-.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.7-3.18-1.73-1.35-.14-2.64.8-3.33.8-.69 0-1.74-.78-2.87-.76-1.47.02-2.83.86-3.59 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.24 2.73 2.2 1.1-.05 1.51-.71 2.84-.71 1.32 0 1.7.71 2.86.69 1.18-.02 1.93-1.07 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.27-.87-2.29-3.5zM14.2 6.13c.6-.73 1.01-1.75.9-2.76-.87.04-1.92.58-2.54 1.3-.56.64-1.05 1.67-.92 2.66.97.08 1.96-.49 2.56-1.2z" /></svg>
        {st?.enabled ? 'Continua con Apple' : 'Apple: da configurare'}
      </button>
      {st && !st.enabled && <p data-testid="auth-apple-reason" className="text-[10px] text-slate-500 text-center t-chat">{st.reason}</p>}
    </div>
  );
}

function EmailForm({ mode }) {
  const { signedIn } = usePlayer();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try { signedIn(mode === 'login' ? await playerApi.login(email, pw) : await playerApi.register(email, pw, name)); }
    catch (x) { setErr(x.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3 mt-3">
      {mode === 'register' && <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome giocatore" required maxLength={40} />}
      <Input data-testid={`auth-${mode}-email`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />
      <Input data-testid={`auth-${mode}-password`} type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={mode === 'register' ? 'Password (min 8 caratteri)' : 'Password'} required minLength={mode === 'register' ? 8 : 1} />
      {err && <div data-testid="auth-error" className="text-xs text-rose-400">{err}</div>}
      <button data-testid={`auth-${mode}-submit`} disabled={busy} className="w-full flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold bg-gradient-to-r from-violet-600 to-cyan-600 disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}{mode === 'login' ? 'Accedi' : 'Crea account (+50 monete)'}
      </button>
    </form>
  );
}

export function AuthDialog() {
  const { brand, features } = useBrand();
  const { authOpen, setAuthOpen } = usePlayer();
  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent data-testid="auth-dialog" className="bg-titan-surface border-violet-500/30 max-w-md">
        <DialogHeader>
          <DialogTitle>Accedi a {brand.name}</DialogTitle>
          <DialogDescription className="t-chat">Salva monete, personaggi sbloccati e record sul tuo account, su ogni dispositivo. Puoi anche continuare come ospite: i progressi dei giochi restano solo su questo dispositivo.</DialogDescription>
        </DialogHeader>
        <GoogleButton />
        <AppleButton />
        <div className="flex items-center gap-3 text-[10px] text-slate-500"><span className="h-px flex-1 bg-white/10" />OPPURE CON EMAIL<span className="h-px flex-1 bg-white/10" /></div>
        <Tabs defaultValue="login">
          <TabsList className={`grid w-full ${features.enableUserRegistration ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <TabsTrigger data-testid="auth-tab-login" value="login">Accedi</TabsTrigger>
            {features.enableUserRegistration && <TabsTrigger data-testid="auth-tab-register" value="register">Registrati</TabsTrigger>}
          </TabsList>
          <TabsContent value="login"><EmailForm mode="login" /></TabsContent>
          <TabsContent value="register"><EmailForm mode="register" /></TabsContent>
        </Tabs>
        <button data-testid="auth-guest-btn" onClick={() => setAuthOpen(false)} className="w-full rounded-xl border border-white/15 py-2.5 text-xs text-slate-300 hover:bg-white/5">Continua come ospite</button>
        <p className="text-[10px] text-slate-500 text-center t-chat">Continuando accetti la <Link to="/legal/privacy" data-testid="auth-privacy-link" onClick={() => setAuthOpen(false)} className="text-cyan-300 underline underline-offset-2">Privacy Policy</Link> e i <Link to="/legal/termini" data-testid="auth-terms-link" onClick={() => setAuthOpen(false)} className="text-cyan-300 underline underline-offset-2">Termini</Link></p>
      </DialogContent>
    </Dialog>
  );
}

export function ProfileButton() {
  const { user, checked, setAuthOpen } = usePlayer();
  if (!checked) return <span className="h-9 w-24" />;
  if (!user) return (
    <button data-testid="profile-login-btn" onClick={() => setAuthOpen(true)} className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold border border-violet-400/50 text-violet-100 hover:bg-violet-500/15 transition-colors">
      <UserRound className="h-4 w-4" />Accedi<span className="hidden sm:inline"> / Profilo</span>
    </button>
  );
  return (
    <Link to="/profilo" data-testid="profile-btn" className="flex items-center gap-2 rounded-full pl-1 pr-3 py-1 border border-white/10 hover:border-violet-400/50 transition-colors">
      {user.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full" /> : <span className="h-7 w-7 rounded-full bg-gradient-to-br from-violet-600 to-cyan-600 flex items-center justify-center text-xs font-bold">{user.name[0]}</span>}
      <span data-testid="profile-coins" className="flex items-center gap-1 text-xs font-bold text-amber-300"><Coins className="h-3.5 w-3.5" />{user.coins}</span>
    </Link>
  );
}
